import { Component, OnInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';

import { HTTPService, HelperService, DataService, DateService, FormatService } from 'app/services';

import { debounce, sortBy } from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { UpdateContactDialogComponent } from '../update-contact-dialog/update-contact-dialog.component';

@Component({
  selector: 'app-follow-up-sidepanel',
  templateUrl: './follow-up-sidepanel.component.html',
  styleUrls: ['./follow-up-sidepanel.component.scss'],
})
export class FollowUpSidepanelComponent implements OnInit, OnDestroy {
  @ViewChild('sidebarContainer', { static: true }) sidebarContainer: ElementRef;

  customerId: string = '';
  orderId: string = '';
  vehicleId: string = '';
  isProcessing: boolean = false;
  isSidebarVisible: boolean = false;
  eligibleFollowUps: any[] = [];
  recommendedFollowUps: any[] = [];
  _eligibleFollowUps: any[] = [];
  _recommendedFollowUps: any[] = [];
  upComingFollowUps: any[] = [];
  pastFollowUps: any[] = [];
  dataListeners: any[] = [];
  vehicles: any[] = [];
  selectedFilter: any = {};
  isScheduling: any = {};
  customer: any = {};
  contact: any = {};
  eventType: string = '';
  scheduledCampaignId: any[] = [];
  isCustomerVehicleEvent: boolean = false;
  clickOutsideListener: Subscription;
  isUpcomingExpanded: boolean = false;
  isEligibleExpanded: boolean = false;
  serverTime: any;
  isMarketingOptOutEnabled: boolean = false;
  isAutomatedFollowUpEnabled: boolean = false;
  isAutomatedFollowUpOnByCustomer: boolean = false;
  hasRecommendedFollowup: boolean = true;

  constructor(
    private dialogService: DialogService,
    private formatService: FormatService,
    private dateService: DateService,
    private dataService: DataService,
    private helperService: HelperService,
    private httpService: HTTPService,
  ) {}

  async ngOnInit() {
    this.setDataListener();
    if (this.isAutomatedFollowUpEnabled && this.isAutomatedFollowUpOnByCustomer) {
      this.hasRecommendedFollowup = false;
    }
  }

  ngOnDestroy() {
    this.dataListeners.forEach((subscribe) => {
      if (subscribe) {
        subscribe.unsubscribe();
      }
    });
  }

  setDataListener() {
    this.isMarketingOptOutEnabled = this.dataService.isMarketingOptOutEnabled;
    this.isAutomatedFollowUpEnabled = this.dataService.isAutomatedFollowUpEnabled;
    this.isAutomatedFollowUpOnByCustomer = this.dataService.isAutomatedFollowUpOnByCustomer;
    this.dataListeners.push(
      this.dataService.getData().subscribe((payload: any) => {
        if (!payload) return;

        const { type, openFrom, data, key } = payload.data;

        if (key === 'company-appdata-updated') {
          this.isMarketingOptOutEnabled = this.dataService.isMarketingOptOutEnabled;
          this.isAutomatedFollowUpEnabled = this.dataService.isAutomatedFollowUpEnabled;
          this.isAutomatedFollowUpOnByCustomer = this.dataService.isAutomatedFollowUpOnByCustomer;
        }

        if (type === 'follow-up-sidebar') {
          this.isSidebarVisible = true;
          this.customerId = data.customerId;
          this.orderId = data.orderId;
          this.eventType = data.event;
          this.isUpcomingExpanded = data && data.viewType === 'view-upcoming';
          this.isEligibleExpanded = data && data.viewType === 'view-eligible';

          if (this.eventType === 'customer_created') {
            this.isEligibleExpanded = true;
            this.vehicleId = '';
            this.selectedFilter = { _id: 'customer-only', name: 'Customer only' };
            this.setSidebarZIndex();
            this.getCustomerFollowUp();
          } else if (this.eventType === 'vehicle_created') {
            this.isEligibleExpanded = true;
            this.vehicleId = data.vehicle._id;
            this.selectedFilter = data.vehicle;
            this.getCustomerFollowUp();
          } else if (this.eventType === 'order_follow_up') {
            this.vehicleId = data.vehicle.vehicleId;
            this.selectedFilter = data.vehicle;
            this.getFollowUp();
          } else {
            this.vehicleId = '';
            this.getAllFollowUps();
            this.selectedFilter = { _id: 'follow-up', name: 'All follow-ups' };
          }

          this.setClickOutsideListener();
          this.getVehicles();
        }
      }),
    );
  }

  /** When user clicks outside of the sidebar */
  setClickOutsideListener() {
    this.clickOutsideListener = this.dataService.getClickEvent().subscribe((target) => {
      const sidebarShade = document.querySelector('.p-sidebar-mask');

      if (sidebarShade && sidebarShade.contains(target)) {
        this.onSidebarOutside();
      }
    });
  }

  /** Removing click outside listener */
  handleUnsubscribleClickoutside() {
    if (this.clickOutsideListener) {
      this.clickOutsideListener.unsubscribe();
    }

    this.clickOutsideListener = null;
  }

  fetchFollowUps = debounce(() => {
    if (this.eventType === 'customer_created') {
      this.getCustomerFollowUp();
      this.selectedFilter = { _id: 'customer-only', name: 'Customer only' };
    } else if (this.eventType === 'vehicle_created') {
      this.vehicleId = this.vehicleId;
      this.getCustomerFollowUp();
    } else if (this.eventType === 'order_follow_up') {
      this.getFollowUp();
    } else {
      this.getAllFollowUps();
      this.selectedFilter = { _id: 'follow-up', name: 'All follow-ups' };
    }
  }, 800);

  async getAllFollowUps() {
    try {
      this.isProcessing = true;
      const [{ campaigns, followUps, customer, serverTime }, { followUps: customerFollowUps }] = await Promise.all([
        this.httpService.getFollowUps(this.customerId, null, null),
        this.httpService.getCustomerVehicleFollowUps(this.customerId, null, {
          fetchCustomer: true,
          fetchVehicle: true,
        }),
      ]);

      this.customer = customer;
      this.contact = customer.contactId;
      this.serverTime = this.dateService.getCurrentDateTimeTz(serverTime);
      this.setUpcomingFollowUp(followUps, customer);
      const { recommendedFollowUps: orderRecommendedFollowUps = [], eligibleFollowUps: orderEligibleFollowUps = [] } = this.formatService.formatOrderFollowUp(
        campaigns,
        customer,
        this.serverTime,
        this.contact,
      );
      const { recommendedFollowUps: customerRecommendedFollowUps = [], eligibleFollowUps: customerEligibleFollowUps = [] } = this.formatService.formatCustomerVehicleFollowUp(
        customerFollowUps,
        customer,
        this.serverTime,
        this.contact,
      );
      this.setRecommendedFollowUp([...orderRecommendedFollowUps, ...customerRecommendedFollowUps], [...orderEligibleFollowUps, ...customerEligibleFollowUps]);

      this.recommendedFollowUps = sortBy(this.recommendedFollowUps, 'scheduleAt');
      this.eligibleFollowUps = sortBy(this.eligibleFollowUps, 'scheduleAt');
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isProcessing = false;
    }
  }

  async getAllFollowUpsOfvhicles() {
    try {
      this.isProcessing = true;
      const [{ campaigns, followUps, customer, serverTime }, { followUps: customerFollowUps }] = await Promise.all([
        this.httpService.getFollowUps(this.customerId, null, this.vehicleId),
        this.httpService.getCustomerVehicleFollowUps(this.customerId, this.vehicleId, {
          fetchCustomer: true,
          fetchVehicle: true,
        }),
      ]);

      this.customer = customer;
      this.contact = customer.contactId;
      this.serverTime = this.dateService.getCurrentDateTimeTz(serverTime);
      this.setUpcomingFollowUp(followUps, customer);
      const { recommendedFollowUps: orderRecommendedFollowUps = [], eligibleFollowUps: orderEligibleFollowUps = [] } = this.formatService.formatOrderFollowUp(
        campaigns,
        customer,
        this.serverTime,
        this.contact,
      );
      const { recommendedFollowUps: customerRecommendedFollowUps = [], eligibleFollowUps: customerEligibleFollowUps = [] } = this.formatService.formatCustomerVehicleFollowUp(
        customerFollowUps,
        customer,
        this.serverTime,
        this.contact,
      );
      this.setRecommendedFollowUp([...orderRecommendedFollowUps, ...customerRecommendedFollowUps], [...orderEligibleFollowUps, ...customerEligibleFollowUps], true);
      this.recommendedFollowUps = sortBy(this.recommendedFollowUps, 'scheduleAt');
      this.eligibleFollowUps = sortBy(this.eligibleFollowUps, 'scheduleAt');
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isProcessing = false;
    }
  }

  async getFollowUp() {
    try {
      this.isProcessing = true;
      const { campaigns, followUps, customer, serverTime } = await this.httpService.getFollowUps(this.customerId, this.eventType === 'order_follow_up' ? this.orderId : null, this.vehicleId);
      this.customer = customer;
      this.contact = customer.contactId;
      this.serverTime = this.dateService.getCurrentDateTimeTz(serverTime);

      this.setUpcomingFollowUp(followUps, customer);
      let { recommendedFollowUps = [], eligibleFollowUps = [] } = this.formatService.formatOrderFollowUp(campaigns, customer, this.serverTime, this.contact);

      this.setRecommendedFollowUp(recommendedFollowUps, eligibleFollowUps);
    } catch (err) {
      this.helperService.showToaster('error', err);
    } finally {
      this.isProcessing = false;
    }
  }

  async getCustomerFollowUp() {
    try {
      const [{ customer, followUps, serverTime }, scheduledFollowUps] = await Promise.all([
        this.httpService.getCustomerVehicleFollowUps(this.customerId, this.vehicleId, {
          fetchCustomer: this.eventType === 'customer_created',
          fetchVehicle: !!this.vehicleId,
        }),
        this.httpService.getScheduledFollowUp(this.customerId, { vehicleId: this.vehicleId, fetchCustomerOnly: this.eventType === 'customer_created' }),
      ]);
      this.customer = customer;
      this.contact = customer.contactId;
      this.serverTime = this.dateService.getCurrentDateTimeTz(serverTime);

      const { recommendedFollowUps = [], eligibleFollowUps = [] } = this.formatService.formatCustomerVehicleFollowUp(followUps, customer, this.serverTime, this.contact);

      this.customer = customer;
      this.setUpcomingFollowUp(scheduledFollowUps, customer);
      this.setRecommendedFollowUp(recommendedFollowUps, eligibleFollowUps);
    } catch (err) {
      this.helperService.showToaster('error', err);
    }
  }

  setUpcomingFollowUp(scheduledFollowUps: any[], customer) {
    const { upcomingFollowUps = [], pastFollowUps = [], upcomingFollowUpIds = [] } = this.formatService.formatScheduledFollowUp(scheduledFollowUps, customer, this.contact);
    this.scheduledCampaignId = [...Array.from(new Set([...this.scheduledCampaignId, ...upcomingFollowUpIds]))];
    this.upComingFollowUps = upcomingFollowUps;

    this.pastFollowUps = pastFollowUps;
  }

  setRecommendedFollowUp(recommendedFollowUps: any[] = [], eligibleFollowUps: any[] = [], vehicle = false) {
    if (vehicle) {
      this.recommendedFollowUps = recommendedFollowUps.filter(({ matchId, vehicle }) => !this.scheduledCampaignId.includes(matchId) && vehicle !== null);
      this.eligibleFollowUps = eligibleFollowUps.filter(({ matchId, vehicle }) => !this.scheduledCampaignId.includes(matchId) && vehicle !== null);
    } else {
      this.recommendedFollowUps = recommendedFollowUps.filter(({ matchId }) => !this.scheduledCampaignId.includes(matchId));
      this.eligibleFollowUps = eligibleFollowUps.filter(({ matchId }) => !this.scheduledCampaignId.includes(matchId));
    }

    this._recommendedFollowUps = this.recommendedFollowUps.slice(0, 10);
    this._eligibleFollowUps = this.eligibleFollowUps.slice(0, 10);
  }

  getVehicles() {
    this.httpService.getVehiclesByCustomer(this.customerId, false, true).then(
      (data) => {
        this.vehicles = [{ _id: 'customer-only', name: 'Customer only' }];

        if (data && data.vehicles) {
          this.vehicles = [...this.vehicles, ...data.vehicles];
        }

        this.vehicles.push({ _id: 'follow-up', name: 'All follow-ups' });

        if (data.customer) {
          this.customer = {
            ...data.customer,
            customerId: data.customer._id,
          };
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      },
    );
  }

  handleResetData() {
    this.handleUnsubscribleClickoutside();
    this.customerId = '';
    this.orderId = '';
    this.vehicleId = '';
    this.isProcessing = false;
    this.isSidebarVisible = false;
    this.eligibleFollowUps = [];
    this.recommendedFollowUps = [];
    this._eligibleFollowUps = [];
    this._recommendedFollowUps = [];
    this.upComingFollowUps = [];
    this.pastFollowUps = [];
    this.dataListeners = [];
    this.vehicles = [];
    this.selectedFilter = {};
    this.isScheduling = {};
    this.customer = {};
    this.eventType = '';
    this.scheduledCampaignId = [];
  }

  handleVehicleSelect(data: any) {
    this.selectedFilter = data;

    if (data._id === 'customer-only') {
      this.vehicleId = '';
      this.eventType = 'customer_created';
      this.getCustomerFollowUp();
    } else if (data._id === 'follow-up') {
      this.vehicleId = '';
      this.getAllFollowUps();
    } else {
      this.vehicleId = data._id;
      this.getAllFollowUpsOfvhicles();
    }
  }

  handleCloseSidebar() {
    this.isSidebarVisible = false;
  }

  onSidebarOutside() {
    this.isSidebarVisible = false;
  }

  handleLoadMore(key: string) {
    if (key === 'recommendedFollowUps') {
      this._recommendedFollowUps = [...this._recommendedFollowUps, ...this.recommendedFollowUps.slice(this._recommendedFollowUps.length, this._recommendedFollowUps.length + 10)];
    } else if (key === 'eligibleFollowUps') {
      this._eligibleFollowUps = [...this._eligibleFollowUps, ...this.eligibleFollowUps.slice(this._eligibleFollowUps.length, this._eligibleFollowUps.length + 10)];
    }
  }

  openContactUpdateDialog() {
    const ref = this.dialogService.open(UpdateContactDialogComponent, {
      closeOnEscape: false,
      width: '650px',
      styleClass: 'add-dialog add-receipt-modal update-contact-dialog',
      showHeader: false,
      data: {
        contactEntityId: this.customer._id,
        email: this.customer.email,
        phone: this.customer.phone,
        contact: this.contact,
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (isSuccess) {
        this.fetchFollowUps();
      }

      onClose.unsubscribe();
    });
  }

  setSidebarZIndex() {
    try {
      setTimeout(() => {
        const sidebar = document.querySelector('#follow-up-sidebar');
        const dialogs = document.querySelectorAll('p-dynamicdialog');
        let highestZIndex = 0;
        if (dialogs && dialogs.length) {
          dialogs.forEach((dialog: any) => {
            const style = dialog.children && dialog.children[0] && dialog.children[0].style;
            if (style.zIndex && Number(style.zIndex) > highestZIndex) {
              highestZIndex = Number(style.zIndex);
            }
          });
        }
        if (highestZIndex) {
          const sidebarContainer: any = sidebar.querySelector('.p-sidebar');
          if (sidebarContainer) {
            const sidebarOverlay: any = document.querySelector('.p-sidebar-mask');
            sidebarOverlay.style.zIndex = highestZIndex;
            sidebarContainer.style.zIndex = `${highestZIndex + 1}`;
          }
        }
      }, 100);
    } catch (err) {
      console.log(err);
    }
  }
}
